import { logs as otelLogs, SeverityNumber, type LogRecord } from "@opentelemetry/api-logs";
import formatLib from "quick-format-unescaped";
import { sem } from "./index.js";

export { SeverityNumber };

type Primitive = string | number | boolean | null;

type Level = "info" | "debug" | "error" | "warn" | "trace" | "fatal";

export const logs = {
    emit: (record: LogRecord) => {
        const logger = otelLogs.getLogger("default");
        logger.emit(record);
    },
    format,
    info: (
        strings: TemplateStringsArray,
        ...values: (Primitive | object | { _tagsOnly: object } | { _tags: object })[]
    ) => {
        log("info", strings, values);
    },
    debug: (
        strings: TemplateStringsArray,
        ...values: (Primitive | object | { _tagsOnly: object } | { _tags: object })[]
    ) => {
        log("debug", strings, values);
    },
    trace: (
        strings: TemplateStringsArray,
        ...values: (Primitive | object | { _tagsOnly: object } | { _tags: object })[]
    ) => {
        log("trace", strings, values);
    },
    warn: (
        strings: TemplateStringsArray,
        ...values: (Primitive | object | { _tagsOnly: object } | { _tags: object })[]
    ) => {
        log("warn", strings, values);
    },
    error:
        (error: Error) =>
        (
            strings: TemplateStringsArray,
            ...values: (Primitive | object | { _tagsOnly: object } | { _tags: object })[]
        ) => {
            log("error", strings, values, error);
        },
    fatal:
        (error: Error) =>
        (
            strings: TemplateStringsArray,
            ...values: (Primitive | object | { _tagsOnly: object } | { _tags: object })[]
        ) => {
            log("fatal", strings, values, error);
        },
};

function log(
    level: Level,
    strings: TemplateStringsArray,
    values: (Primitive | object | { _tagsOnly: object } | { _tags: object })[],
    error?: Error,
): void {
    const { message, attributes } = format(level, strings, values, error);
    logs.emit({
        severityNumber:
            level === "debug"
                ? SeverityNumber.DEBUG
                : level === "error"
                  ? SeverityNumber.ERROR
                  : level === "info"
                    ? SeverityNumber.INFO
                    : level === "warn"
                      ? SeverityNumber.WARN
                      : SeverityNumber.TRACE,
        severityText: level,
        body: message,
        timestamp: performance.now(),
        observedTimestamp: performance.now(),
        attributes,
    });
}

function format(level: Level, strings: TemplateStringsArray, values: (Primitive | object)[], error?: Error) {
    const parts: string[] = [];
    const args2: unknown[] = [];
    for (let i = 0; i < strings.length; i++) {
        parts.push(strings[i] as string);
        if (i < values.length) {
            const arg = values[i];
            if (typeof arg === "number") {
                parts.push("%d");
                args2.push(arg);
            } else if (arg && typeof arg === "object") {
                parts.push("%o");
                args2.push(arg);
            } else {
                parts.push("%s");
                args2.push(arg);
            }
        }
    }
    const messageTemplate = parts.join("");
    return {
        level,
        message: messageTemplate,
        attributes: {
            ...(error
                ? {
                      [sem.ATTR_EXCEPTION_TYPE]: error.name,
                      [sem.ATTR_EXCEPTION_STACKTRACE]: error.stack,
                      [sem.ATTR_EXCEPTION_MESSAGE]: error.message,
                      "exception.cause": error.cause ? JSON.stringify(error.cause) : undefined,
                  }
                : {}),
            formatted: formatLib(messageTemplate, args2),
        },
    };
}
